import type { Router } from 'vue-router'
import { useClipboard } from '@vueuse/core'
import { isIOS, isChrome } from '@/env'
import { useSystemInfo, useUserInfo } from '@/store'
import type { iGame, iGameCategory } from '@/types/index'
import bcrypt from 'bcryptjs'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { FINGER_PRINT_KEY, SALT_KEY } from '@/consts'
import dayjs from 'dayjs'
import { handleSaqueClick } from '@/utils/userStatusUtils'

export const lowPriorityAsyncCall = (fn: () => void) => {
  let callFn: (fn: () => void) => void
  if ('requestIdleCallback' in window) {
    callFn = requestIdleCallback
  } else {
    callFn = setTimeout
  }
  callFn(fn)
}

export const wait = (seconds: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, seconds * 1000)
  })
}

export const downloadFile = (url, fileName) => {
  const a = document.createElement('a')
  a.href = url
  a.target = '_self'
  a.download = fileName
  a.click()
}

const isFromDrainageWebsite = () => {
  const drainageArr = ['facebook', 'instagram']
  return drainageArr.some((item) => document.referrer.includes(item))
}

export const launchChrome = () => {
  if (isChrome) return

  const params = new URLSearchParams(location.search)
  params.append('isFromDrainageWebsite', isFromDrainageWebsite() ? '1' : '0')
  const url =
    location.href.split('?')[0].replace(/http(s)?:\/\//, '') + `?${params}`

  if (isIOS) {
    // 是iOS模板页才启动Chrome
    const isTemplate = /template/i.test(window.location.href)
    if (isTemplate) window.location.href = 'googlechrome://' + url
  } else {
    window.location.href = `intent://${url}#Intent;package=com.android.chrome;scheme=http;end;`
  }
}

export const gotoActivity = (item, router: Router) => {
  router.push({
    path: item.link,
    query: {
      activityId: item.activityId,
    },
    state: {
      // currentActivity: toRaw(item)
    },
  })
}

export const handleJumpLink = (item, router) => {
  if (item.hyperlinkType == 'NONE') return

  if (item.hyperlink) {
    if (item.hyperlinkType === 'CUSTOM_LINK') {
      if (item.jumpMethod === 'EXTERNAL_JUMP') {
        window.open(item.hyperlink, '_blank')
      } else {
        window.location.href = item.hyperlink
      }
    } else {
      if (item.hyperlink === '/saque') {
        // 提款页先展示弹窗
        // updateIsOpenStatus();
        //
        handleSaqueClick(router)
      } else {
        gotoActivity(
          {
            activityId: item.activityId,
            link: item.hyperlink,
          },
          router,
        )
      }
    }
  }
}

export const gotoGameCategory = (item: iGameCategory, router: Router) => {
  router.push({
    path: '/gamecategory',
    query: {
      cid: item.id,
    },
    state: {
      // @ts-ignore
      currentCategory: toRaw(item),
    },
  })
}

export const gotoGame = (item: iGame, router: Router) => {
  if (useUserInfo().isLoggedin) {
    router.push(`/game?gameCode=${item.code}`)
  } else {
    router.push('/login')
  }
}

export const formatter = (mindigits = 2, maxDigits = 2) => {
  const langStr = useSystemInfo().langStr

  return new Intl.NumberFormat(langStr, {
    style: 'decimal',
    minimumFractionDigits: mindigits,
    maximumFractionDigits: maxDigits,
  })
}

export const copyText = (text: string) => {
  const { copy } = useClipboard()
  copy(text)
    .then(() => {
      showToast({
        icon: 'success',
        iconSize: '30px',
        message: 'foi copiado',
      })
    })
    .catch(() => {
      showToast({
        icon: 'fail',
        message: 'Failed to copy text',
      })
    })
}

//密码加密
export const encryptPassword = (password: string) => {
  return bcrypt.hashSync(password, SALT_KEY)
}

export const cryptoID = (eventName: string) => {
  return eventName + '-' + localStorage.getItem('pwaId')
}

export async function generateFingerprint() {
  const fp = await FingerprintJS.load()
  const result = await fp.get()
  localStorage.setItem(FINGER_PRINT_KEY, result.visitorId)
  return result.visitorId
}

// 格式化金额
export const formatNumber = (value: number, mindigits = 2, maxDigits = 2) => {
  const number = parseFloat(value as any)

  if (isNaN(number)) {
    return value // 如果不是有效数字，直接返回原值
  }

  if (number >= 1000000) {
    const formattedValue = number / 1000000
    return `${formatter(mindigits, maxDigits).format(formattedValue)}M`
  }

  if (number >= 1000) {
    const formattedValue = number / 1000
    return `${formatter(mindigits, maxDigits).format(formattedValue)}K`
  }

  return formatter(mindigits, maxDigits).format(value)
}

// 1. 前端本地引用 local://live.png 把local://替换成本地图片路径后打开
// 2. OSS存储引用 oss://a/b/live.png 把oss://替换成OSS图片地址后打开
// 3. HTTP直接 http://xxxx.com/a/b/live.png 直接打开图片
export const getImageUrl = (url: string) => {
  const { imageBaseUrl } = useSystemInfo()
  const localPath = `/static/images/defaults/`
  return url.replace('local://', localPath).replace('oss://', imageBaseUrl)
}

// 获取日期范围
export const getDateRange = (range: string) => {
  const today = dayjs()
  console.log('today', today.valueOf())
  const ranges = {
    Hoje: [today.startOf('day').valueOf(), today.endOf('day').valueOf()],
    Ontem: [
      today.subtract(1, 'day').startOf('day').valueOf(),
      today.subtract(1, 'day').endOf('day').valueOf(),
    ],
    'Esta semana': [
      today.startOf('week').valueOf(),
      today.endOf('week').valueOf(),
    ],
    'Este mês': [
      today.startOf('month').valueOf(),
      today.endOf('month').valueOf(),
    ],
    'Mês passado': [
      today.subtract(1, 'month').startOf('month').valueOf(),
      today.subtract(1, 'month').endOf('month').valueOf(),
    ],
    'Últimos três meses': [
      today.subtract(90, 'day').startOf('day').valueOf(),
      today.endOf('day').valueOf(),
    ],
  }

  return ranges[range] || ranges['Hoje']
}

// 格式化成巴西时间格式
export const formatDateInPortuguese = (dateString) => {
  const months = [
    'jan.',
    'fev.',
    'mar.',
    'abr.',
    'mai.',
    'jun.',
    'jul.',
    'ago.',
    'set.',
    'out.',
    'nov.',
    'dez.',
  ]
  const date = dayjs(dateString)
  const day = date.date()
  const month = months[date.month()]
  const year = date.year()
  const hours = date.hour().toString().padStart(2, '0')
  const minutes = date.minute().toString().padStart(2, '0')
  const seconds = date.second().toString().padStart(2, '0')

  return `${day} de ${month} de ${year} ${hours}:${minutes}:${seconds}`
}

export const useFormattedDate = (dateString) => {
  const formatDateInPortuguese = (date) => {
    const months = [
      'jan.',
      'fev.',
      'mar.',
      'abr.',
      'mai.',
      'jun.',
      'jul.',
      'ago.',
      'set.',
      'out.',
      'nov.',
      'dez.',
    ]
    const day = date.date()
    const month = months[date.month()]
    const year = date.year()

    return `${day} de ${month} de ${year}`
  }

  const date = dayjs(dateString)
  const formattedDate = formatDateInPortuguese(date)

  return formattedDate
}

export const fetchWithLoading = async (
  fn: () => Promise<any>,
  {
    loadingText = 'loading...',
    errorText = 'error',
    successText = '',
    onSuccess = () => {},
  }: {
    loadingText?: string
    errorText?: string
    successText?: string
    onSuccess?: () => void
  } = {},
) => {
  showLoadingToast(loadingText)
  try {
    const res = await fn()
    if (res.code == 1) {
      if (successText) {
        showSuccessToast({
          iconSize: 30,
          message: successText,
          onClose: onSuccess,
        })
      } else {
        closeToast()
      }
    } else {
      showToast(res.msg)
    }
    return res
  } catch (error) {
    showToast(errorText)
  }
}

// 次数
export const formatCount = (v, unit = 'K') => {
  if (v < 1000) {
    return v
  } else {
    return Math.floor(v / 1000) + unit
  }
}
